"use client"

import { useSession } from "@/hooks/useSession"
import type { NovaSession } from "@/types"
import { useParams, usePathname, useSelectedLayoutSegments } from "next/navigation"
import React, { useEffect } from "react"
import { ampli } from "../ampli"

const NEXT_PUBLIC_AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY!

interface Props {
  serverSession: NovaSession | null
}

export const Tracking: React.FC<Props> = ({ serverSession }) => {
  const { data: clientSession } = useSession()
  const pathname = usePathname()
  const [previousRoute, setPreviousRoute] = React.useState<string | null>(null)
  const segments = useSelectedLayoutSegments()

  const isLoaded = ampli.isLoaded

  const params = useParams()
  useEffect(() => {
    const genericSegments = segments?.map(segment => {
      return (params && Object.entries(params).find(([key, value]) => value === segment)?.[0]) || segment
    })
    const genericName = genericSegments?.join("-").replace("(", "").replace(")", "") || "?"

    if (previousRoute !== genericName && isLoaded) {
      if (genericName && previousRoute !== null) {
        // Tracking navigation events
        ampli.navigation({
          from: previousRoute,
          to: genericName,
        })
      }
      setPreviousRoute(genericName)

      ampli.screenView({
        screen_name: genericName,
      })
    }
  }, [setPreviousRoute, previousRoute, pathname, segments, isLoaded, params])

  useEffect(() => {
    if (!ampli.isLoaded) {
      ampli.load({
        client: {
          apiKey: NEXT_PUBLIC_AMPLITUDE_API_KEY,
          configuration: {
            defaultTracking: true,
          },
        },
      })
    }
  }, [clientSession])

  useEffect(() => {
    const setUserProperties = async () => {
      if (serverSession?.user.uuid) {
        ampli.identify(serverSession?.user.uuid)
      }

      if (serverSession?.user.creative.id || serverSession?.user.client.id) {
        if (serverSession?.user.creative.id) {
          ampli.identify(serverSession?.user.uuid, {
            "Creative ID": serverSession?.user.creative.id,
          })
        }
        if (serverSession?.user.client.id) {
          ampli.identify(serverSession?.user.uuid, {
            "Client ID": serverSession?.user.client.id,
          })
        }
      }
    }

    if (isLoaded && (serverSession?.user.uuid || serverSession?.user.creative.id || serverSession?.user.client.id)) {
      setUserProperties()
    }
  }, [isLoaded, serverSession])

  return null
}
